import { useState, useEffect } from 'react';

// Client-side pagination for a card grid. The list endpoints return everything
// in one response, so this slices in the browser — no backend change, and cards
// off the current page never render, so their images never download.
//
// Page size follows the 900px breakpoint the dashboard layout already switches
// on, so it tracks the grid's column count instead of adding a second one.
export const PAGE_BREAKPOINT = 900;

const matches = () =>
    typeof window !== 'undefined' && window.matchMedia(`(max-width: ${PAGE_BREAKPOINT}px)`).matches;

export function usePaged(list, { narrow = 4, wide = 8, resetKey } = {}) {
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(() => (matches() ? narrow : wide));

    // Follow the breakpoint live, so rotating a phone or resizing a desktop
    // window re-pages rather than leaving a stale count.
    useEffect(() => {
        const mq = window.matchMedia(`(max-width: ${PAGE_BREAKPOINT}px)`);
        const apply = () => setPageSize(mq.matches ? narrow : wide);
        apply();
        mq.addEventListener('change', apply);
        return () => mq.removeEventListener('change', apply);
    }, [narrow, wide]);

    // Changing filter/section (or shrinking the page size) returns to page 1 —
    // otherwise a narrower list leaves you looking at an empty grid.
    useEffect(() => { setPage(1); }, [resetKey, pageSize]);

    const total = list.length;
    const pageCount = Math.max(1, Math.ceil(total / pageSize));
    // Clamp rather than storing a corrected page: removing the last item on the
    // final page would otherwise strand the view on an empty one.
    const currentPage = Math.min(page, pageCount);

    return {
        items: list.slice((currentPage - 1) * pageSize, currentPage * pageSize),
        page: currentPage,
        pageCount,
        setPage,
        pageSize,
        total,
    };
}
